// Loads what's needed to work out which vehicles are due an admin inspection or camera check, and records camera checks.
import { supabase } from "@/lib/supabase";
import { isDraftFresh } from "@/lib/adminInspectionProgress";
import {
  CHECK_SETTING_NAMES,
  buildVehicleChecks,
  localIsoDay,
  parseCheckSettings,
  type CheckSettings,
  type VehicleCheckRow,
} from "@/lib/vehicleChecks";

export interface CameraCheckRecord {
  id: string;
  vehicle_number: string;
  checked_on: string;
  checked_by: string | null;
  notes: string | null;
}

/** The signed-in person's name, as the rest of the app reads it from local storage. */
export function loggedInUserName(): string {
  for (const key of ["loggedUser", "currentUser"]) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const u = JSON.parse(raw);
      if (u?.name) return String(u.name);
      if (u?.driver_name) return String(u.driver_name);
    } catch {
      /* ignore a malformed entry */
    }
  }
  return "";
}

const byFleetNumber = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true });

/**
 * Every tracked vehicle with where it stands on admin inspections and camera checks.
 * @param onlyVehicles limit to these fleet numbers (a restricted user sees only their linked vehicles); null = all
 */
export async function loadVehicleChecks(
  onlyVehicles?: string[] | null
): Promise<{ rows: VehicleCheckRow[]; settings: CheckSettings }> {
  const [settingsRes, vehiclesRes, adminRes, cameraRes, draftRes] = await Promise.all([
    supabase.from("settings").select("name, value").in("name", [...CHECK_SETTING_NAMES]),
    supabase
      .from("vehicles")
      .select(
        "vehicle_number, registration, status, has_camera, camera_type, camera_storage, camera_id, admin_inspection_interval_months, camera_check_interval_months"
      ),
    // Only completed inspections count: one that was started and abandoned must not read as "just checked".
    supabase
      .from("vehicle_inspections")
      .select("vehicle_number, created_at")
      .eq("inspection_type", "admin")
      .not("completed_at", "is", null)
      .order("created_at", { ascending: false })
      .limit(5000),
    supabase
      .from("vehicle_camera_checks")
      .select("vehicle_number, checked_on")
      .order("checked_on", { ascending: false })
      .limit(5000),
    // Admin inspections that have been started (some progress saved) but not finished.
    supabase
      .from("vehicle_inspections")
      .select("vehicle_number, created_at, progress_pct")
      .eq("inspection_type", "admin")
      .is("completed_at", null)
      .gt("progress_pct", 0)
      .order("created_at", { ascending: false })
      .limit(500),
  ]);

  for (const res of [vehiclesRes, adminRes, cameraRes, draftRes]) {
    if (res.error) throw res.error;
  }

  const settings = parseCheckSettings(settingsRes.data);

  // Rows arrive newest first, so the first one seen for a vehicle is its latest.
  const lastAdmin = new Map<string, string>();
  for (const r of adminRes.data ?? []) {
    if (!lastAdmin.has(r.vehicle_number)) lastAdmin.set(r.vehicle_number, localIsoDay(r.created_at));
  }
  const lastCamera = new Map<string, string>();
  for (const r of cameraRes.data ?? []) {
    if (!lastCamera.has(r.vehicle_number)) lastCamera.set(r.vehicle_number, String(r.checked_on).slice(0, 10));
  }

  const inProgress = new Map<string, number>();
  for (const r of draftRes.data ?? []) {
    if (!inProgress.has(r.vehicle_number) && isDraftFresh(r.created_at)) inProgress.set(r.vehicle_number, Number(r.progress_pct));
  }

  let vehicles = vehiclesRes.data ?? [];
  if (onlyVehicles) {
    const allowed = new Set(onlyVehicles);
    vehicles = vehicles.filter((v) => allowed.has(v.vehicle_number));
  }

  const rows = buildVehicleChecks(vehicles, lastAdmin, lastCamera, settings, localIsoDay(new Date()), inProgress);
  rows.sort((a, b) => byFleetNumber(a.vehicle_number, b.vehicle_number));
  return { rows, settings };
}

export async function loadCameraChecks(vehicleNumber: string, limit = 8): Promise<CameraCheckRecord[]> {
  const { data, error } = await supabase
    .from("vehicle_camera_checks")
    .select("id, vehicle_number, checked_on, checked_by, notes")
    .eq("vehicle_number", vehicleNumber)
    .order("checked_on", { ascending: false })
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as CameraCheckRecord[];
}

export async function recordCameraCheck(input: {
  vehicle_number: string;
  checked_on: string;
  checked_by: string;
  notes: string;
}): Promise<void> {
  const { error } = await supabase.from("vehicle_camera_checks").insert({
    vehicle_number: input.vehicle_number,
    checked_on: input.checked_on,
    checked_by: input.checked_by.trim() || null,
    notes: input.notes.trim() || null,
  });
  if (error) throw error;
}

export async function deleteCameraCheck(id: string): Promise<void> {
  const { error } = await supabase.from("vehicle_camera_checks").delete().eq("id", id);
  if (error) throw error;
}
